import {SmileySad, WarningCircle} from "@phosphor-icons/react";
import {BaseButton, BaseSkeleton, BaseSpinner} from "@/components";
import CatalogCard from "./CatalogCard";
import type {GameItem} from "./useGamesCatalog";

const gridCls =
  "grid grid-cols-3 min-[640px]:grid-cols-4 min-[960px]:grid-cols-5 min-[1280px]:grid-cols-6 min-[1600px]:grid-cols-7 " +
  "gap-3 min-[640px]:gap-4 mt-4";

const SKELETON_KEYS = ["sk-1", "sk-2", "sk-3", "sk-4", "sk-5", "sk-6", "sk-7", "sk-8", "sk-9", "sk-10"];

interface FavGame {
  game_id: string;
  game_provider: string;
}

interface CatalogGridProps {
  games: GameItem[];
  loading: boolean;
  error: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  favoriteGames: FavGame[];
  showingCount: number;
  onLoadMore: () => void;
  onRetry: () => void;
}

const CatalogGrid = ({
  games,
  loading,
  error,
  hasMore,
  isLoadingMore,
  favoriteGames,
  showingCount,
  onLoadMore,
  onRetry,
}: CatalogGridProps) => {
  if (loading) {
    return (
      <div
        role="status"
        aria-label="Cargando juegos"
        aria-busy="true"
        className={gridCls}
      >
        {SKELETON_KEYS.map(key => (
          <div
            key={key}
            className="flex flex-col gap-2 items-center w-full"
          >
            <BaseSkeleton
              className="!block w-full"
              height={180}
              radius={12}
            />
            <BaseSkeleton
              width={100}
              height={12}
            />
            <BaseSkeleton
              width={64}
              height={10}
            />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex flex-col items-center gap-5 py-20 text-center"
        role="alert"
      >
        <WarningCircle
          size={52}
          color="var(--color-text-tertiary)"
          aria-hidden="true"
        />
        <div>
          <p className="m-0 text-base font-semibold text-[var(--color-text)]">No pudimos cargar los juegos</p>
          <p className="m-0 mt-1.5 text-sm text-[var(--color-text-tertiary)]">
            Verifica tu conexión e intenta nuevamente
          </p>
        </div>
        <BaseButton
          variant="primary"
          size="sm"
          onClick={onRetry}
        >
          Reintentar
        </BaseButton>
      </div>
    );
  }

  if (games.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 py-20 text-center">
        <SmileySad
          size={52}
          color="var(--color-text-tertiary)"
          aria-hidden="true"
        />
        <div>
          <p className="m-0 text-base font-semibold text-[var(--color-text)]">Sin resultados</p>
          <p className="m-0 mt-1.5 text-sm text-[var(--color-text-tertiary)]">
            No hay juegos que coincidan con los filtros aplicados.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={gridCls}>
        {games.map(game => (
          <CatalogCard
            key={game.id ?? `${game.name}-${game.provider}`}
            gameId={game.id}
            name={game.name}
            provider={game.provider}
            icon={game.icon}
            isFav={favoriteGames.some(fav => fav.game_id === game.id && fav.game_provider === game.provider)}
          />
        ))}
      </div>

      {hasMore && (
        <div className="flex flex-col items-center gap-2 mt-10 mb-10">
          {isLoadingMore ? (
            <BaseSpinner size="sm" />
          ) : (
            <button
              type="button"
              className="btn-load-more"
              onClick={onLoadMore}
            >
              Cargar más juegos
            </button>
          )}
          <p
            className="m-0 text-xs text-[var(--color-text-tertiary)]"
            aria-live="polite"
            aria-atomic="true"
          >
            Mostrando {showingCount} juegos
          </p>
        </div>
      )}
    </>
  );
};

export default CatalogGrid;
